import { onBeforeUnmount, onMounted, type Ref } from 'vue';

/**
 * The record form's first editable field: a kit text input that is neither
 * read-only nor disabled, visible, and not part of a table or a dialog.
 */
export const FIRST_FIELD_SELECTOR = '.input-wrapper > input:not([readonly]):not(:disabled)';

/** How long after the page opened the focus may still be placed. */
const ARM_MS = 10_000;

export function findFirstField(root: HTMLElement): HTMLInputElement | null {
  for (const input of root.querySelectorAll<HTMLInputElement>(FIRST_FIELD_SELECTOR)) {
    if (input.closest('[role="grid"], [role="dialog"], dialog, [inert], [aria-hidden="true"]'))
      continue;
    if (input.getClientRects().length === 0) continue;
    return input;
  }
  return null;
}

/** True while nobody holds the focus — the only time it is ours to place. */
function focusIsFree(): boolean {
  const active = document.activeElement;
  return !active || active === document.body || active === document.documentElement;
}

/**
 * Puts the focus into the record's first editable field once the page has
 * opened (checklist „Bedienlogik“ 1.1, I-2833): the fields arrive with the
 * record, so it watches the form area until one is there. Every app's record
 * page uses it (the ERP's ShowLayout, the admin's detail pages).
 *
 * It never takes the focus from anyone: only while the focus sits on the body
 * (a list row that opened the record is gone by then), and it stops for good at
 * the first key or pointer the user presses, or after a few seconds.
 */
export function useFocusFirstField(root: Ref<HTMLElement | null>, enabled: () => boolean): void {
  let observer: MutationObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let frame = 0;

  function stop(): void {
    observer?.disconnect();
    observer = null;
    if (timer) clearTimeout(timer);
    timer = null;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    window.removeEventListener('keydown', stop, true);
    window.removeEventListener('pointerdown', stop, true);
  }

  function tryFocus(): void {
    frame = 0;
    const el = root.value;
    if (!el || !focusIsFree()) return;
    const field = findFirstField(el);
    if (!field) return;
    field.focus({ preventScroll: true });
    // A field the record's refetch renders anew drops the focus back on the
    // body; the observer stays until the window closes and catches that too.
  }

  function schedule(): void {
    if (!frame) frame = requestAnimationFrame(tryFocus);
  }

  onMounted(() => {
    if (!enabled() || !root.value) return;
    observer = new MutationObserver(schedule);
    observer.observe(root.value, { childList: true, subtree: true, attributes: true });
    window.addEventListener('keydown', stop, true);
    window.addEventListener('pointerdown', stop, true);
    timer = setTimeout(stop, ARM_MS);
    schedule();
  });
  onBeforeUnmount(stop);
}
